import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PanelLoading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { Select } from "../../../components/Select";
import { useAuth } from "../../../auth/AuthContext";
import { localizedName } from "../../../lib/localizedName";
import { formatDate } from "../../../lib/format";
import type { ApiError } from "../../../api/client";
import {
  externalAnalysisApi,
  externalLabApi,
  type ExternalAnalysis,
  type ExternalLaboratory,
  type ExternalPurpose,
} from "../../../api/externalAnalyses";
import ExternalAnalysisForm from "./ExternalAnalysisForm";

/**
 * Nalazi spoljnih laboratorija.
 *
 * Dva su posla na ovom ekranu, i drugi je razlog zašto prvi uopšte vredi. Prijavi se nalaz — koja
 * laboratorija, koji predmet, koje vrednosti, i sam izveštaj. A onda se te vrednosti mogu pretražiti i
 * uporediti, što se sa samim PDF-om ne može.
 *
 * Arhiv je pokazao koliko to košta kad ne postoji: mikrobiologija je 2025. naručena 1.396 puta i izmerena
 * nijedan put, jer se radi spolja, a nalaz je stizao u obliku koji njihov program ne čita.
 */
export default function ExternalAnalysesPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const lang = i18n.resolvedLanguage ?? "sr";
  const smeDaUnosi = hasPermission("external_analyses:manage");

  const [labs, setLabs] = useState<ExternalLaboratory[]>([]);
  const [rows, setRows] = useState<ExternalAnalysis[] | null>(null);
  const [laboratoryId, setLaboratoryId] = useState("");
  const [purpose, setPurpose] = useState<"" | ExternalPurpose>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      setRows(
        await externalAnalysisApi.search({
          laboratoryId: laboratoryId || undefined,
          purpose: purpose || undefined,
          from: from || undefined,
          to: to || undefined,
          q: q.trim() || undefined,
        }),
      );
    } catch (e) {
      toast.error((e as ApiError).message);
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laboratoryId, purpose, from, to, q]);

  useEffect(() => {
    void externalLabApi
      .list(true)
      .then(setLabs)
      .catch(() => setLabs([]));
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function purposeLabel(p: ExternalPurpose): string {
    if (p === "FOR_CUSTOMER") return t("external.purpose.FOR_CUSTOMER", "za kupca");
    if (p === "DISPUTE") return t("external.purpose.DISPUTE", "superanaliza");
    return t("external.purpose.INTERNAL", "interno");
  }

  async function download(a: ExternalAnalysis) {
    try {
      const { url } = await externalAnalysisApi.downloadUrl(a.id);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  /** Koliko nalaza nosi i vrednosti, a koliko je ostalo samo fajl. To je jedina ocena ovog ekrana. */
  const presek = useMemo(() => {
    const all = rows ?? [];
    return {
      ukupno: all.length,
      saVrednostima: all.filter((a) => a.values.length > 0).length,
      saFajlom: all.filter((a) => a.hasFile).length,
      kupcu: all.filter((a) => a.purpose === "FOR_CUSTOMER").length,
    };
  }, [rows]);

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("external.title", "Spoljne analize")}</h1>
          <div className="sub">
            {t(
              "external.lead",
              "Nalazi laboratorija van Bankoma. Uz svaki ide i šta je izmereno, ne samo izveštaj — inače se posle ne može ni pretražiti ni uporediti.",
            )}
          </div>
        </div>
        {smeDaUnosi && !adding && (
          <button type="button" className="btn primary" onClick={() => setAdding(true)}>
            {t("external.new", "Nov nalaz")}
          </button>
        )}
      </div>

      {smeDaUnosi && adding &&
        (labs.length === 0 ? (
          <div className="card">
            <div className="card-b">
              <div className="empty">
                {t(
                  "external.noLabsYet",
                  "Šifarnik laboratorija je prazan. Direktor kvaliteta prvo upisuje laboratorije koje Bankom koristi.",
                )}
              </div>
              <button type="button" className="btn ghost" onClick={() => setAdding(false)}>
                {t("common.cancel", "Odustani")}
              </button>
            </div>
          </div>
        ) : (
          <ExternalAnalysisForm
            laboratories={labs}
            onCancel={() => setAdding(false)}
            onSaved={() => {
              setAdding(false);
              void load();
            }}
          />
        ))}

      <div className="card">
        <div className="card-b">
          <div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
            <Select
              value={laboratoryId}
              onChange={setLaboratoryId}
              placeholder={t("external.allLabs", "sve laboratorije")}
              options={[
                { value: "", label: t("external.allLabs", "sve laboratorije") },
                ...labs.map((l) => ({ value: l.id, label: l.name })),
              ]}
            />
            <Select
              value={purpose}
              onChange={(v) => setPurpose(v as "" | ExternalPurpose)}
              placeholder={t("external.allPurposes", "sve namene")}
              options={[
                { value: "", label: t("external.allPurposes", "sve namene") },
                { value: "INTERNAL", label: t("external.purpose.INTERNAL", "interno") },
                { value: "FOR_CUSTOMER", label: t("external.purpose.FOR_CUSTOMER", "za kupca") },
                { value: "DISPUTE", label: t("external.purpose.DISPUTE", "superanaliza") },
              ]}
            />
            <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            <input
              className="input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("external.search", "predmet ili broj izveštaja")}
            />
            <button type="button" className="btn sm" onClick={() => void load()}>
              {t("common.search", "Traži")}
            </button>
          </div>
        </div>
      </div>

      {rows === null ? (
        <PanelLoading text={t("external.loading", "Čitam nalaze…")} />
      ) : rows.length === 0 ? (
        <div className="empty">{t("external.none", "Nema prijavljenih nalaza za ove uslove.")}</div>
      ) : (
        <>
          <div className="kpi-grid">
            <div className="card">
              <div className="card-b">
                <div className="muted">{t("external.kpi.total", "nalaza")}</div>
                <strong style={{ fontSize: 22 }}>{presek.ukupno}</strong>
              </div>
            </div>
            <div className="card">
              <div className="card-b">
                <div className="muted">{t("external.kpi.withValues", "sa unetim vrednostima")}</div>
                <strong style={{ fontSize: 22 }}>{presek.saVrednostima}</strong>
              </div>
            </div>
            <div className="card">
              <div className="card-b">
                <div className="muted">{t("external.kpi.withFile", "sa izveštajem")}</div>
                <strong style={{ fontSize: 22 }}>{presek.saFajlom}</strong>
              </div>
            </div>
            <div className="card">
              <div className="card-b">
                <div className="muted">{t("external.kpi.forCustomer", "pušteno kupcu")}</div>
                <strong style={{ fontSize: 22 }}>{presek.kupcu}</strong>
              </div>
            </div>
          </div>

          <table className="tbl">
            <thead>
              <tr>
                <th>{t("external.col.issuedOn", "Datum")}</th>
                <th>{t("external.col.lab", "Laboratorija")}</th>
                <th>{t("external.col.subject", "Predmet")}</th>
                <th>{t("external.col.number", "Broj")}</th>
                <th>{t("external.col.purpose", "Namena")}</th>
                <th>{t("external.col.values", "Vrednosti")}</th>
                <th>{t("external.col.file", "Izveštaj")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <Fragment key={a.id}>
                  <tr
                    onClick={() => setOpen(open === a.id ? null : a.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{formatDate(a.issuedOn ?? a.recordedAt, lang)}</td>
                    <td>{a.laboratoryName ?? "—"}</td>
                    <td>{a.subject}</td>
                    <td>{a.reportNumber ?? "—"}</td>
                    <td>
                      <span className={`tag ${a.purpose === "FOR_CUSTOMER" ? "warn" : "neutral"}`}>
                        {purposeLabel(a.purpose)}
                      </span>
                    </td>
                    <td>
                      {a.values.length === 0 ? (
                        <span className="tag warn">{t("external.onlyFile", "samo fajl")}</span>
                      ) : (
                        a.values.length
                      )}
                    </td>
                    <td>
                      {a.hasFile ? (
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            void download(a);
                          }}
                        >
                          {t("external.open", "Otvori")}
                        </button>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                  {open === a.id && (
                    <tr>
                      <td colSpan={7}>
                        {a.values.length === 0 ? (
                          <div className="empty">
                            {t(
                              "external.noValuesYet",
                              "Nijedna vrednost nije uneta iz ovog izveštaja, pa nalaz ne ulazi ni u pretragu ni u kretanje parametra.",
                            )}
                          </div>
                        ) : (
                          <table className="tbl">
                            <thead>
                              <tr>
                                <th>{t("external.col.parameter", "Parametar")}</th>
                                <th>{t("external.col.value", "Vrednost")}</th>
                                <th>{t("external.col.note", "Napomena")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {a.values.map((v) => (
                                <tr key={v.parameterId}>
                                  <td>{localizedName({ name: v.key, nameI18n: v.nameI18n }, lang)}</td>
                                  <td>
                                    {v.valueNum != null
                                      ? `${v.valueNum.toFixed(v.decimals)}${v.unit ? ` ${v.unit}` : ""}`
                                      : v.valueBool != null
                                        ? v.valueBool
                                          ? t("common.yes", "da")
                                          : t("common.no", "ne")
                                        : (v.valueText ?? "—")}
                                  </td>
                                  <td className="muted">{v.note ?? ""}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        {a.note && <div className="muted">{a.note}</div>}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
